/**
 * use-theme-density.ts — React hook exposing `{ theme, density, setTheme, setDensity }`.
 *
 * Source of truth = `document.documentElement` attributes (`data-theme` /
 * `data-density`). The FOWT inline script sets them on first paint;
 * `applyTheme` / `applyDensity` / `reconcileWithBackend` update them
 * afterwards. The hook subscribes via `useSyncExternalStore` to the
 * `linkhr:prefs-changed` CustomEvent, so toggling in one component
 * re-renders every other consumer instantly.
 *
 * Snapshot caching: `useSyncExternalStore` demands referentially stable
 * snapshots when values haven't changed — otherwise React thrashes. We
 * memoize in a module-scoped cache keyed on the (theme, density) tuple.
 *
 * SSR safety: `getServerSnapshot` returns the default prefs since
 * `document` is undefined on the server; the hook boots into
 * `{ light, comfortable }` and hydrates to the real DOM state on mount.
 */
import { useSyncExternalStore, useCallback } from "react";
import { applyTheme, applyDensity, type Theme, type Density } from "./theme-density";

const PREFS_CHANGED_EVENT = "linkhr:prefs-changed";

function subscribe(onChange: () => void): () => void {
  window.addEventListener(PREFS_CHANGED_EVENT, onChange);
  return () => window.removeEventListener(PREFS_CHANGED_EVENT, onChange);
}

function getSnapshot(): { theme: Theme; density: Density } {
  const theme = (document.documentElement.getAttribute("data-theme") as Theme) ?? "light";
  const density =
    (document.documentElement.getAttribute("data-density") as Density) ?? "comfortable";
  return { theme, density };
}

let cachedSnapshot: { theme: Theme; density: Density } = {
  theme: "light",
  density: "comfortable",
};

function getCachedSnapshot(): { theme: Theme; density: Density } {
  const fresh = getSnapshot();
  if (fresh.theme !== cachedSnapshot.theme || fresh.density !== cachedSnapshot.density) {
    cachedSnapshot = fresh;
  }
  return cachedSnapshot;
}

function getServerSnapshot(): { theme: Theme; density: Density } {
  return { theme: "light", density: "comfortable" };
}

export interface UseThemeDensityResult {
  theme: Theme;
  density: Density;
  setTheme: (theme: Theme) => void;
  setDensity: (density: Density) => void;
}

export function useThemeDensity(): UseThemeDensityResult {
  const { theme, density } = useSyncExternalStore(subscribe, getCachedSnapshot, getServerSnapshot);
  const setTheme = useCallback((t: Theme) => {
    applyTheme(t);
    window.dispatchEvent(new CustomEvent(PREFS_CHANGED_EVENT));
  }, []);
  const setDensity = useCallback((d: Density) => {
    applyDensity(d);
    window.dispatchEvent(new CustomEvent(PREFS_CHANGED_EVENT));
  }, []);
  return { theme, density, setTheme, setDensity };
}
